/**
 * Clarity One customer access readiness — shared types and pure helpers.
 * Staff-facing diagnostics only; never expose these fields on public OTP responses.
 */
import { maskWhatsAppDisplay, displayWhatsAppFromNormalized } from "./proposalApprovalOtp";
import { normalisePhone } from "./phoneUtils";

/** Visible resend cooldown after a successful OTP send attempt (seconds). */
export const CLARITY_ONE_OTP_RESEND_COOLDOWN_SECONDS = 30;

export type ClarityOnePhoneUniqueness =
  | "unique"
  | "ambiguous"
  | "conflicting"
  | "missing"
  | "invalid"
  | "alias_only";

export type ClarityOneOtpEligibility =
  | "eligible"
  | "portal_disabled"
  | "phone_missing"
  | "phone_invalid"
  | "phone_ambiguous"
  | "phone_conflict"
  | "merged_hidden"
  | "not_canonical";

export type ClarityOneAccessBlockerCode =
  | "none"
  | "portal_disabled"
  | "phone_missing"
  | "phone_invalid"
  | "phone_ambiguous"
  | "phone_conflict"
  | "merged_duplicate"
  | "hidden_non_canonical"
  | "never_activated"
  | "otp_delivery_failed"
  | "otp_sent_unverified"
  | "no_eligible_warranty"
  | "wrong_or_merged_record";

export type ClarityOneCustomerAccessReadiness = {
  customerId: number;
  customerName: string;
  canonicalCustomerId: number;
  isCanonical: boolean;
  isMergedDuplicate: boolean;
  isHiddenFromList: boolean;
  clarityOneEnabled: boolean;
  registeredPhonePresent: boolean;
  registeredPhoneRaw: string | null;
  phoneNormalized: string | null;
  phoneNormalizedValid: boolean;
  phoneUniqueness: ClarityOnePhoneUniqueness;
  conflictingCustomerIds: number[];
  otpEligible: boolean;
  otpEligibility: ClarityOneOtpEligibility;
  customerWarrantyItemCount: number;
  lastGenuineCustomerLoginAt: Date | string | null;
  lastStaffImpersonationAt: Date | string | null;
  latestOtpRequestAt: Date | string | null;
  latestOtpRequestResult: string | null;
  latestOtpDeliveryAt: Date | string | null;
  latestOtpDeliveryResult: string | null;
  latestOtpVerificationAt: Date | string | null;
  latestOtpVerificationResult: string | null;
  blockerCode: ClarityOneAccessBlockerCode;
  blockerSummary: string;
  recommendedStaffAction: string;
};

export function maskClarityOnePhoneForDisplay(rawOrNormalized: string | null | undefined): string {
  const normalized = normalisePhone(rawOrNormalized);
  if (!normalized) return "•••• ••••";
  return maskWhatsAppDisplay(normalized);
}

export function displayClarityOnePhone(rawOrNormalized: string | null | undefined): string | null {
  const normalized = normalisePhone(rawOrNormalized);
  if (!normalized) return null;
  return displayWhatsAppFromNormalized(normalized);
}

export function classifyAccessBlocker(input: {
  isCanonical: boolean;
  isMergedDuplicate: boolean;
  isHiddenFromList: boolean;
  clarityOneEnabled: boolean;
  phoneNormalizedValid: boolean;
  registeredPhonePresent: boolean;
  phoneUniqueness: ClarityOnePhoneUniqueness;
  otpEligible: boolean;
  customerWarrantyItemCount: number;
  lastGenuineCustomerLoginAt: Date | string | null;
  latestOtpDeliveryResult: string | null;
  latestOtpVerificationResult: string | null;
  latestOtpRequestResult: string | null;
}): { blockerCode: ClarityOneAccessBlockerCode; blockerSummary: string; recommendedStaffAction: string } {
  if (input.isMergedDuplicate || (!input.isCanonical && input.isHiddenFromList)) {
    return {
      blockerCode: "merged_duplicate",
      blockerSummary: "Customer is attached to a merged/hidden duplicate record.",
      recommendedStaffAction:
        "Open the canonical customer record and confirm warranty ownership + Clarity One enablement there.",
    };
  }

  if (!input.isCanonical) {
    return {
      blockerCode: "wrong_or_merged_record",
      blockerSummary: "This record is not the canonical customer identity.",
      recommendedStaffAction: "Use the canonical customer identity for portal access and warranty ownership.",
    };
  }

  if (!input.clarityOneEnabled) {
    return {
      blockerCode: "portal_disabled",
      blockerSummary: "Clarity One is disabled for this customer.",
      recommendedStaffAction: "Enable Clarity One on the canonical customer after confirming a unique mobile number.",
    };
  }

  if (!input.registeredPhonePresent) {
    return {
      blockerCode: "phone_missing",
      blockerSummary: "OTP cannot be sent because no registered phone is present.",
      recommendedStaffAction: "Add a unique mobile number with country code, then ask the customer to request OTP login.",
    };
  }

  if (!input.phoneNormalizedValid) {
    return {
      blockerCode: "phone_invalid",
      blockerSummary: "OTP cannot be sent because the registered phone is invalid for WhatsApp OTP login.",
      recommendedStaffAction: "Correct the contact to a valid mobile number with country code, then retry customer OTP login.",
    };
  }

  if (input.phoneUniqueness === "ambiguous" || input.phoneUniqueness === "conflicting") {
    return {
      blockerCode: input.phoneUniqueness === "conflicting" ? "phone_conflict" : "phone_ambiguous",
      blockerSummary: "OTP identity is ambiguous — the normalised phone maps to multiple active customers.",
      recommendedStaffAction:
        "Resolve duplicate phone ownership so only one active canonical customer owns this number. Do not guess.",
    };
  }

  if (!input.otpEligible) {
    return {
      blockerCode: "portal_disabled",
      blockerSummary: "Customer is not currently OTP-eligible.",
      recommendedStaffAction: "Review portal enablement, phone uniqueness, and canonical identity before customer login.",
    };
  }

  if (input.latestOtpDeliveryResult === "otp_send_failed") {
    return {
      blockerCode: "otp_delivery_failed",
      blockerSummary: "OTP provider delivery failed on the latest send attempt.",
      recommendedStaffAction:
        "Check WhatsApp template / provider status. Ask the customer to request OTP again after the cooldown; do not use staff impersonation as proof of login.",
    };
  }

  if (
    input.latestOtpRequestResult &&
    !input.lastGenuineCustomerLoginAt &&
    input.latestOtpVerificationResult !== "otp_verified"
  ) {
    if (input.latestOtpDeliveryResult === "otp_sent") {
      return {
        blockerCode: "otp_sent_unverified",
        blockerSummary: "OTP was sent but never verified — customer has not completed genuine login.",
        recommendedStaffAction:
          "Ask the customer to open the WhatsApp code and complete verification. Staff impersonation does not count as activation.",
      };
    }
    return {
      blockerCode: "never_activated",
      blockerSummary: "Warranty exists but the customer has never completed a genuine Clarity One OTP login.",
      recommendedStaffAction:
        "Confirm phone readiness above, then ask the customer to request OTP at one.claritygroup.com.sg.",
    };
  }

  if (input.lastGenuineCustomerLoginAt && input.customerWarrantyItemCount === 0) {
    return {
      blockerCode: "no_eligible_warranty",
      blockerSummary: "Customer logged in genuinely but has no eligible issued warranty items.",
      recommendedStaffAction:
        "Confirm warranty card status is issued/review_required and originalWarrantyHolderCustomerId matches this canonical customer.",
    };
  }

  if (!input.lastGenuineCustomerLoginAt) {
    return {
      blockerCode: "never_activated",
      blockerSummary: "Warranty exists but the customer has never completed a genuine Clarity One OTP login.",
      recommendedStaffAction:
        "Customer access is login-ready on paper; ask them to complete WhatsApp OTP login. Staff impersonation is not customer activation.",
    };
  }

  return {
    blockerCode: "none",
    blockerSummary: "No Clarity One access blocker detected for this customer.",
    recommendedStaffAction: "No action required for login readiness.",
  };
}
